import {
  Collapse,
  Divider,
  FormControl,
  FormControlLabel,
  List,
  Radio,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from '@mui/material';
import { RestoreMethod } from '@repo/api-gw-sdk';

import { Icon } from '@/components/shared/icon';
import { OptionListItem } from '@/components/shared/optionListItem';

import { DirectRestoreWriteModeSelector } from './directRestoreWriteModeSelector';

export const AutomaticRestoreMethodSection = ({
  restoreMethod,
  setRestoreMethod,
  disabledMethods,
  writeMode,
  setWriteMode,
  writeCapacityUnits,
  setWriteCapacityUnits,
}: {
  restoreMethod: RestoreMethod;
  setRestoreMethod: (value: RestoreMethod) => void;
  disabledMethods: RestoreMethod[];
  writeMode: 'Provisioned' | 'On-demand';
  setWriteMode: (value: 'Provisioned' | 'On-demand') => void;
  writeCapacityUnits: number;
  setWriteCapacityUnits: (value: number) => void;
}) => {
  const s3RestoreDisabled = disabledMethods.includes(
    RestoreMethod.RESTORE_METHOD_IMPORT_TABLE
  );

  return (
    <Stack className='w-full px-[40px]' gap='24px'>
      <Stack
        className='group/autoRestore'
        direction='row'
        justifyContent='space-between'
        alignItems='center'
      >
        <Typography variant='subtitle1'>
          Use automatic restore method
        </Typography>
        <Stack direction='row' alignItems='center' gap='8px'>
          <Tooltip
            title={
              'Let Eon automatically adjust the best restore method for your table size and account limits. Turn off to select it manually.'
            }
          >
            <Icon
              className='opacity-0 group-hover/autoRestore:opacity-100 transition-opacity'
              iconClass='material-symbols-help-outline-rounded'
            />
          </Tooltip>
          <Switch
            size='small'
            checked={restoreMethod === RestoreMethod.RESTORE_METHOD_AUTO}
            onChange={() => {
              setRestoreMethod(
                restoreMethod === RestoreMethod.RESTORE_METHOD_AUTO
                  ? s3RestoreDisabled
                    ? RestoreMethod.RESTORE_METHOD_CAPACITY_BASED
                    : RestoreMethod.RESTORE_METHOD_IMPORT_TABLE
                  : RestoreMethod.RESTORE_METHOD_AUTO
              );
            }}
          />
        </Stack>
      </Stack>
      {restoreMethod !== RestoreMethod.RESTORE_METHOD_AUTO && (
        <Stack gap='24px'>
          <Typography variant='body1'>Select restore method</Typography>
          <List>
            <OptionListItem
              data-testid='importViaS3Mode'
              isChecked={
                restoreMethod === RestoreMethod.RESTORE_METHOD_IMPORT_TABLE
              }
              onClick={() => {
                return s3RestoreDisabled
                  ? undefined
                  : setRestoreMethod(RestoreMethod.RESTORE_METHOD_IMPORT_TABLE);
              }}
            >
              <FormControl disabled={s3RestoreDisabled}>
                <FormControlLabel
                  checked={
                    restoreMethod === RestoreMethod.RESTORE_METHOD_IMPORT_TABLE
                  }
                  control={<Radio />}
                  label={
                    <Stack gap='4px'>
                      <Typography variant='subtitle1'>Import via S3</Typography>
                      <Typography variant='body2'>
                        No write capacity consumed.
                      </Typography>
                    </Stack>
                  }
                />
              </FormControl>
            </OptionListItem>
            <OptionListItem
              data-testid='directRestoreMode'
              isChecked={
                restoreMethod === RestoreMethod.RESTORE_METHOD_CAPACITY_BASED
              }
              onClick={() =>
                setRestoreMethod(RestoreMethod.RESTORE_METHOD_CAPACITY_BASED)
              }
            >
              <FormControlLabel
                checked={
                  restoreMethod === RestoreMethod.RESTORE_METHOD_CAPACITY_BASED
                }
                control={<Radio />}
                label={
                  <Stack gap='4px'>
                    <Typography variant='subtitle1'>Direct restore</Typography>
                    <Typography variant='body2'>
                      Writes data directly to your table.
                    </Typography>
                  </Stack>
                }
              />
              <Collapse
                className='w-full'
                in={
                  restoreMethod === RestoreMethod.RESTORE_METHOD_CAPACITY_BASED
                }
              >
                <Stack padding='24px 36px'>
                  <Divider sx={{ borderStyle: 'dashed' }} />
                  <DirectRestoreWriteModeSelector
                    writeMode={writeMode}
                    setWriteMode={setWriteMode}
                    writeCapacityUnits={writeCapacityUnits}
                    setWriteCapacityUnits={setWriteCapacityUnits}
                  />
                </Stack>
              </Collapse>
            </OptionListItem>
          </List>
        </Stack>
      )}
    </Stack>
  );
};
